import { fetchJSON as fetchAPI } from './client';

export type ExperienceDashboardSnapshot = {
  tenant_id: string; experience_score: number; consistency_score: number;
  accessibility_score: number; cognitive_load_score: number; motion_score: number;
  performance_score: number; design_qa_pass_rate_pct: number; pages_audited: number;
  wcag_compliance_pct: number; improvement_trend: string; generated_at: string;
};

export type PrinciplesSnapshot = {
  feel_like: string[]; never_like: string[]; product_persona: string;
  emotions_create: string[]; emotions_avoid: string[]; inspiration: string[];
};

export type VisualDesignSnapshot = {
  typography_scale: string; base_spacing_unit: string; grid_columns: number;
  corner_radius: string; primary_color: string; dark_mode_supported: boolean;
  visual_hierarchy_score: number; whitespace_score: number;
};

export type LayoutSystemSnapshot = {
  grid_columns: number; max_content_width: string; sidebar_width: string;
  card_padding: string; dashboard_grid_rules: string; responsive_breakpoints: number;
  layout_consistency_pct: number;
};

export type AccessibilitySnapshot = {
  wcag_level: string; keyboard_only_pass: boolean; screen_reader_pass: boolean;
  high_contrast_pass: boolean; reduced_motion_pass: boolean;
  accessible_forms_pct: number; accessible_charts_pct: number; compliance_pct: number;
};

export type CognitiveLoadSnapshot = {
  avg_decisions_per_page: number; avg_clicks_to_goal: number;
  context_switches_per_session: number; recognition_over_recall: boolean; load_score: number;
};

export type MotionDesignSnapshot = {
  default_duration_ms: number; default_easing: string; reduce_motion_supported: boolean;
  page_transition_ms: number; skeleton_animation: boolean; motion_compliance_pct: number;
};

export type DesignQASnapshot = {
  pages_passed: number; pages_failed: number; consistency_issues: number;
  alignment_issues: number; spacing_issues: number; accessibility_issues: number; pass_rate_pct: number;
};

export type UsabilityMetricsSnapshot = {
  task_completion_pct: number; avg_time_to_complete_sec: number; error_rate_pct: number;
  navigation_efficiency: number; nps: number; csat: number; feature_usage_pct: number;
};

export type RecommendationUXSnapshot = {
  business_value_visible: boolean; evidence_visible: boolean; confidence_visible: boolean;
  approve_reject_actions: boolean; explain_action: boolean; expected_roi_visible: boolean;
  adoption_rate_pct: number;
};

export type CopywritingSnapshot = {
  voice: string; tone_attributes: string[]; avoid_attributes: string[];
  microcopy_guidelines: boolean; error_copy_standards: boolean; copy_consistency_score: number;
};

const COMMAND_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/command';
const fetchC = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const experienceDesignApi = {
  dashboard: () => fetchC<ExperienceDashboardSnapshot>('/experience-design/dashboard'),
  principles: () => fetchC<PrinciplesSnapshot>('/experience-design/principles'),
  visualDesign: () => fetchC<VisualDesignSnapshot>('/experience-design/visual-design'),
  layout: () => fetchC<LayoutSystemSnapshot>('/experience-design/layout'),
  accessibility: () => fetchC<AccessibilitySnapshot>('/experience-design/accessibility'),
  cognitiveLoad: () => fetchC<CognitiveLoadSnapshot>('/experience-design/cognitive-load'),
  motion: () => fetchC<MotionDesignSnapshot>('/experience-design/motion'),
  designQA: () => fetchC<DesignQASnapshot>('/experience-design/design-qa'),
  usability: () => fetchC<UsabilityMetricsSnapshot>('/experience-design/usability'),
  recommendations: () => fetchC<RecommendationUXSnapshot>('/experience-design/recommendations'),
  copywriting: () => fetchC<CopywritingSnapshot>('/experience-design/copywriting'),
};
